import { Model } from '../../Redux/api/modelApi'

/**
 * Хук для расчета стоимости аренды оборудования
 * Выполняет комплексные расчеты с учетом всех факторов: топливо, доставка, проживание, питание и др.
 */

export interface OfferModelUnit {
  id?: number
  relocationRoute?: string
  relocationCost?: number
  departurePoint?: string
  workPoint?: string
  returnPoint?: string
  deliveryCost?: number
  returnCost?: number
  quantity?: number
  shiftCount?: number
  toolId?: number
  totalRentPricePerHour?: number
  tool?: {
    id: number
    name: string
    toolWorkPrice?: number
    description?: string | null
  }
}

export interface ModelCalculated extends Model {
  totalRentPricePerHour?: number
  specialPrice?: number
  shiftsCount?: number
  route?: string
  relocationCost?: number
  offerCount?: number
  selectedTool?: number | undefined
  detalization?: boolean
  selectedImageUrl?: string
  units?: OfferModelUnit[]
}

// Константы для расчета
const DAYS_IN_MONTH = 30 // количество дней в месяц (постоянное значение)
export const WORK_HOURS_PER_MONTH = 300 // количество часов в месяц (постоянное значение)

export type GetTotalRentOptions = {
  model: ModelCalculated
  fuelPrice: number
  deliveryPrice: number
  personDeliveryPrice: number
  accommodationPrice: number
  foodPrice: number
  personnelHouseholdServicesCost: number
  medicalExaminationCost: number
  relocationCost?: number
  selectedToolPrice?: number
  dailyMedCheckCost?: number
  season: string
  // Количество смен экземпляра; если не задано — берётся из модели
  shiftsCount?: number
}

interface UseCalculator {
  getTotalRent: (options: GetTotalRentOptions) => number
}
export const useCalculator = (): UseCalculator => {
  const getTotalRent = ({
    model,
    fuelPrice,
    deliveryPrice,
    personDeliveryPrice,
    accommodationPrice,
    foodPrice,
    personnelHouseholdServicesCost,
    medicalExaminationCost,
    selectedToolPrice = 0,
    dailyMedCheckCost = 0,
    season,
    shiftsCount: shiftsCountOverride,
  }: GetTotalRentOptions): number => {
    // Количество смен: экземпляра (если задано), иначе модели, по умолчанию 1.
    // Ноль недопустим — на него делится итоговая стоимость часа
    const shiftsCount = shiftsCountOverride || model?.shiftsCount || 1

    // Определяем цену за час (специальная цена или базовая себестоимость)
    const price =
      model.specialPrice && model.specialPrice > 0
        ? model.specialPrice
        : model.baseRentalCost

    // Количество рабочих часов в месяц в зависимости от количества смен
    // количество часов в месяц * количество смен работы = количество рабочих часов в месяц
    const workHoursMonthShifts = shiftsCount * WORK_HOURS_PER_MONTH

    // Количество рабочих дней в месяц в зависимости от количества смен
    // количество дней в месяц * количество смен работы = количество рабочих дней
    const workDaysMonthShifts = shiftsCount * DAYS_IN_MONTH

    // Себестоимость аренды в месяц
    // себестоимость аренды за 1 маш час * количество рабочих часов в месяц = Себестоимость аренды в месяц
    const equipmentRawRentPrice = price * workHoursMonthShifts

    // Стоимость доставки запчастей - указывается сразу за месяц
    const partsAndConsumablesDeliveryCost = Number(deliveryPrice)

    // Стоимость доставки персонала к месту производства работ за месяц
    // стоимость билета на 1 сотрудника * количество смен работы = стоимость доставки персонала к месту производства работ за месяц
    const personnelDeliveryCostPerPerson = shiftsCount * personDeliveryPrice

    // Стоимость проживания в месяц
    // стоимость проживания сотрудника в сутки * количество рабочих дней = Стоимость проживания в месяц
    const accommodationCostPerPerson = workDaysMonthShifts * accommodationPrice

    // Стоимость питания в месяц
    // стоимость питания в сутки * количество рабочих дней = стоимость питания в месяц
    const foodCostTotal = workDaysMonthShifts * foodPrice

    // Стоимость бытового обслуживания в месяц
    // стоимость бытового обслуживания * количество рабочих дней = Стоимость бытового обслуживания
    const personnelHouseholdServicesCostTotal =
      workDaysMonthShifts * personnelHouseholdServicesCost

    // Стоимость медосмотров в месяц * количество смен
    const medicalExaminationTotal = medicalExaminationCost * shiftsCount

    // Дополнительная стоимость ежедневных медосмотров
    const dailyMedCheckCostTotal = dailyMedCheckCost * workDaysMonthShifts

    // Стоимость надбавки за навесное оборудование в месяц
    // стоимость надбавки за 1 маш час * количество рабочих часов в месяц = стоимость надбавки за навесное оборудование
    const selectedToolPriceTotal = selectedToolPrice * workHoursMonthShifts

    // Определяем расход топлива в зависимости от сезона
    const isWinter = season === 'winter'
    const currentFuelConsumption = isWinter
      ? model.fuelEfficiencyWinter
      : model.fuelEfficiencySummer

    // Расход топлива за месяц работ
    // расход топлива в час * стоимость топлива за литр * количество рабочих часов в месяц = расход топлива за месяц работ
    const fuelConsumption =
      currentFuelConsumption * fuelPrice * workHoursMonthShifts

    // Итого в месяц (без топлива)
    // Себестоимость аренды в месяц + Стоимость надбавки за навесное оборудование в месяц +
    // Стоимость доставки запчастей + Стоимость доставки персонала к месту производства работ за месяц +
    // Стоимость проживания в месяц + Стоимость питания в месяц + Стоимость бытового обслуживания в месяц +
    // Стоимость медосмотров + Стоимость перемещения + Дополнительные медосмотры = Промежуточная стоимость аренды в месяц
    const rentSubtotal =
      equipmentRawRentPrice +
      partsAndConsumablesDeliveryCost +
      personnelDeliveryCostPerPerson +
      accommodationCostPerPerson +
      foodCostTotal +
      personnelHouseholdServicesCostTotal +
      medicalExaminationTotal +
      // relocationCost +
      selectedToolPriceTotal +
      dailyMedCheckCostTotal

    // Итоговая стоимость аренды в час
    // (Промежуточная стоимость аренды в месяц + Расход топлива за месяц работ) / количество рабочих часов в месяц = итоговая стоимость аренды в час
    const total = Math.ceil(
      (rentSubtotal + fuelConsumption) / workHoursMonthShifts,
    )

    return Number(total)
  }

  return {
    getTotalRent,
  }
}
